class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  connectedCallback() {
    this.onViewportResize = () => {
      if (this.isOpen) this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    };
    window.visualViewport?.addEventListener('resize', this.onViewportResize);
    window.addEventListener('resize', this.onViewportResize);
    this.onOutsidePointer = event => { if (!this.contains(event.target)) this.close(); };
    document.addEventListener('pointerdown', this.onOutsidePointer);
    this.onEscape = event => { if (event.key === 'Escape') this.close(); };
    this.addEventListener('keydown', this.onEscape);
  }

  disconnectedCallback() {
    window.visualViewport?.removeEventListener('resize', this.onViewportResize);
    window.removeEventListener('resize', this.onViewportResize);
    document.removeEventListener('pointerdown', this.onOutsidePointer);
    this.removeEventListener('keydown', this.onEscape);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', () => {
      if (!this.getQuery() && !this.isOpen) this.showSearchIdeas();
    });
    this.input.addEventListener('input', () => {
      if (this.getQuery() && this.querySelector('[data-search-ideas]')) {
        this.close();
        this.predictiveSearchResults.innerHTML = '';
        this.removeAttribute('results');
      }
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.showSearchIdeas();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || (!event.submitter?.classList.contains('dut-search-all') && this.querySelector('[aria-selected="true"] a'))) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.showSearchIdeas();
    }
  }

  showSearchIdeas() {
    if (!window.DutSmartSearch) return;
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    const ideas = ['Dildo under $100', 'Rechargeable under $100', 'Waterproof vibrators', 'Water-based lube'];
    const links = ideas.map((label, index) => '<li role="option" aria-selected="false" id="search-idea-' + this.input.id + '-' + index + '"><a href="' + window.DutSmartSearch.url(label).replace(/&/g, '&amp;') + '">' + label + ' <span aria-hidden="true">↗</span></a></li>').join('');
    this.renderSearchResults('<div data-search-ideas><div class="dut-search-toolbar"><span class="dut-search-heading">Try a search</span><button type="button" class="dut-search-browse">Hide keyboard</button><button type="button" class="dut-search-close" aria-label="Close search results">×</button></div><div id="predictive-search-results" role="listbox" aria-label="Suggested searches"><div class="dut-search-sidebar"><section><h2 class="dut-search-heading">Search by product, feature or budget</h2><ul>' + links + '</ul></section></div></div><span class="hidden" data-predictive-search-live-region-count-value>4 suggested searches</span></div>');
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) { this.showSearchIdeas(); return; }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Mobile keyboard dismissal can move focus to body; keep suggestions browsable.
      if (document.activeElement === document.body && matchMedia('(max-width: 989px)').matches) return;
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length && !this.querySelector('[data-search-ideas]')) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const label = this.querySelector('[data-predictive-search-search-for-text]');
    if (label) label.textContent = `View all results for “${newTerm}”`;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    if (!activeElement) return;
    activeElement.scrollIntoView({ block: 'nearest' });
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(window.DutSmartSearch?.parse(searchTerm).q || searchTerm)}&resources[type]=product,collection,article,query&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=hide&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        if (this.getQuery() !== searchTerm) return;
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (window.DutSmartSearch) {
      const budget = window.DutSmartSearch.parse(this.getQuery());
      if (budget.min !== null || budget.max !== null) {
        const fragment = document.createElement('template');
        fragment.innerHTML = resultsMarkup;
        fragment.content.querySelectorAll('[data-search-price]').forEach(item => {
          const price = Number(item.dataset.searchPrice) / 100;
          if ((budget.min !== null && price < Number(budget.min)) || (budget.max !== null && price > Number(budget.max))) item.remove();
        });
        const products = fragment.content.querySelector('.dut-search-products');
        if (products && !products.querySelector('[data-search-price]')) {
          products.innerHTML = '<li class="dut-search-empty">No quick matches in this budget. View all results to search the full catalogue.</li>';
        }
        const label = fragment.content.querySelector('[data-predictive-search-search-for-text]');
        if (label) label.textContent = 'View all results for “' + this.getQuery() + '”';
        resultsMarkup = fragment.innerHTML;
      }
    }
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const resultCount = this.predictiveSearchResults.querySelector('[data-total-results]');
    if (resultCount) {
      const displayedCount = this.predictiveSearchResults.querySelectorAll('[role="option"]').length;
      resultCount.dataset.totalResults = displayedCount;
      resultCount.textContent = `${displayedCount} suggestions for ${this.getQuery()}`;
    }
    const browse = this.querySelector('.dut-search-browse');
    browse.addEventListener('click', () => {
      this.input.blur();
      browse.focus({ preventScroll: true });
      this.onViewportResize?.();
    });
    this.querySelector('.dut-search-close').addEventListener('click', () => {
      this.close();
      const modal = this.closest('details-modal');
      if (modal) modal.close();
      else { this.input.focus({ preventScroll: true }); this.close(); }
    });
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    this.resultsMaxHeight = Math.max(80, bottom - this.predictiveSearchResults.getBoundingClientRect().top - 12);
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute('open', true);
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
